"use client";

import PublicLayout from "@/components/layout/PublicLayout";
import {
  Search,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  Star,
  Globe,
  Navigation,
  Award,
  Clock,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { getCurrentPosition, sortByDistance, geocodePostcode } from "@/lib/geolocation";

interface GarageImage {
  id: string;
  url: string;
  alt?: string | null;
  sortOrder: number;
}

interface Garage {
  id: string;
  name: string;
  slug?: string | null;
  postcode?: string | null;
  city?: string | null;
  county?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number;
  reviewCount?: number;
  images?: GarageImage[];
  garageimage?: GarageImage[];
}

export default function GaragesClient() {
  const router = useRouter();
  const [garages, setGarages] = useState<Garage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [findingLocation, setFindingLocation] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState("");
  const [searchingPostcode, setSearchingPostcode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const GARAGES_PER_PAGE = 12;

  useEffect(() => {
    fetchGarages();

    const onFocus = () => {
      fetchGarages();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchGarages();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const fetchGarages = async () => {
    try {
      const res = await fetch("/api/garages?publicOnly=true", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        const normalized = Array.isArray(data)
          ? data.map((garage: any) => ({
              ...garage,
              images: Array.isArray(garage.images)
                ? garage.images
                : Array.isArray(garage.garageimage)
                ? garage.garageimage
                : [],
            }))
          : [];
        setGarages(normalized);
      }
    } catch (error) {
      console.error("Failed to fetch garages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFindNearest = async () => {
    setFindingLocation(true);
    setCurrentPage(1);
    try {
      const position = await getCurrentPosition();
      setUserLocation(position);
      setSearchTerm(""); // Clear text search when using location
    } catch (error) {
      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String((error as any).message);
      }
      console.error("Geolocation error:", errorMessage);
      alert("Could not get your location. Please enable location access or try searching by postcode.");
    } finally {
      setFindingLocation(false);
    }
  };

  const clearLocation = () => {
    setUserLocation(null);
    setPostcodeSearch("");
  };

  const handleCardNavigate = (
    event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    targetSlug?: string | null,
    targetId?: string
  ) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a,button")) return;
    const slug = targetSlug || targetId;
    if (slug) {
      router.push(`/garages/${slug}`);
    }
  };

  const filteredGarages = garages.filter((garage) =>
    [garage.name, garage.postcode, garage.address, garage.city, garage.county]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const garagesWithCoordinates = filteredGarages.map((garage) => ({
    ...garage,
    latitude: garage.latitude ?? null,
    longitude: garage.longitude ?? null,
  }));

  // Sort by distance if user location is available
  const displayGarages = userLocation
    ? sortByDistance(garagesWithCoordinates, userLocation.latitude, userLocation.longitude)
    : garagesWithCoordinates.map((g) => ({ ...g, distance: null }));

  // Pagination
  const totalPages = Math.ceil(displayGarages.length / GARAGES_PER_PAGE);
  const startIndex = (currentPage - 1) * GARAGES_PER_PAGE;
  const endIndex = startIndex + GARAGES_PER_PAGE;
  const paginatedGarages = displayGarages.slice(startIndex, endIndex);

  return (
    <PublicLayout>
      <div className="bg-slate-50 min-h-screen font-sans text-slate-900 overflow-x-hidden">

        {/* ── 1. HERO SECTION (MATCHING HOMEPAGE & FEATURES BRANDING) ────────── */}
        <section className="relative overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 text-white pt-12 pb-16 md:pt-16 md:pb-20">
          {/* Ambient background glow */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-5">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
              VERIFIED UK WORKSHOPS
            </span>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight text-white tracking-tight">
              Find Trusted UK{" "}
              <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                Garages & MOT Stations
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-normal">
              Discover verified automotive service centers, MOT testing stations, and mobile mechanics powered by GarageBoost across the UK.
            </p>

            {/* Search Input Bar */}
            <div className="pt-2 max-w-2xl mx-auto">
              <div className="bg-white p-2 rounded-2xl shadow-2xl border border-slate-700/50 flex items-center gap-2">
                <div className="relative flex-1 flex items-center">
                  <Search className="absolute left-3.5 text-slate-400 pointer-events-none" size={20} />
                  <input
                    type="text"
                    id="garage-search"
                    name="garageSearch"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (e.target.value) clearLocation();
                    }}
                    placeholder="Search by garage name, city, area, or postcode..."
                    className="w-full pl-11 pr-4 py-3 text-slate-900 text-sm sm:text-base font-medium placeholder:text-slate-400 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleFindNearest}
                  disabled={findingLocation}
                  className="bg-slate-900 hover:bg-blue-600 text-white p-3 sm:px-4 sm:py-3 rounded-xl transition-all flex items-center gap-2 text-xs sm:text-sm font-bold shrink-0 cursor-pointer disabled:opacity-50"
                  title="Find nearest using my location"
                >
                  <MapPin size={16} className={findingLocation ? "animate-spin text-teal-400" : "text-teal-400"} />
                  <span className="hidden sm:inline">
                    {findingLocation ? "Locating..." : "Near Me"}
                  </span>
                </button>
              </div>

              {userLocation && (
                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-teal-300">
                  <span>📍 Sorted by proximity to your current location</span>
                  <button
                    onClick={clearLocation}
                    className="underline hover:text-white cursor-pointer ml-1"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-400 pt-1">
              ✓ Verified MOT stations • ✓ Direct contact details • ✓ Real customer reviews
            </p>
          </div>
        </section>

        {/* ── 2. TRUST STATS BAR ────────────────────────────────────────────── */}
        <section className="py-6 bg-slate-900 border-b border-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-800 text-center">
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-white">Verified</p>
                <p className="text-xs text-slate-400 mt-0.5">UK registered workshops</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-blue-400">DVSA Data</p>
                <p className="text-xs text-slate-400 mt-0.5">Direct MOT integration</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-teal-400">Direct Booking</p>
                <p className="text-xs text-slate-400 mt-0.5">Phone, email & quotes</p>
              </div>
              <div className="py-2 px-4">
                <p className="text-xl sm:text-2xl font-extrabold text-cyan-400">Nationwide</p>
                <p className="text-xs text-slate-400 mt-0.5">England, Scotland & Wales</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. GARAGES DIRECTORY GRID ──────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
              <p className="text-slate-600 font-bold text-sm">Loading verified garages...</p>
            </div>
          ) : displayGarages.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border-2 border-slate-200 shadow-sm max-w-xl mx-auto p-8">
              <div className="bg-blue-50 text-blue-600 rounded-full w-16 h-16 mx-auto flex items-center justify-center mb-4 border border-blue-100">
                <MapPin size={32} />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">No garages found</h2>
              <p className="text-sm text-slate-600 mb-6">
                {searchTerm
                  ? `No garages match "${searchTerm}". Try a different city or postcode.`
                  : "No public garages are currently listed."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl hover:bg-blue-700 transition-colors text-sm"
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Header result counter */}
              <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900">
                    Explore Garages ({displayGarages.length})
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {userLocation ? "Sorted by nearest distance" : "Showing all verified UK garages"}
                  </p>
                </div>
              </div>

              {/* Grid of Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {paginatedGarages.map((garage) => {
                  const firstImage =
                    garage.images && garage.images.length > 0 ? garage.images[0] : null;

                  return (
                    <div
                      key={garage.id}
                      className="group bg-white rounded-3xl overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-slate-200/90 hover:border-blue-300 flex flex-col justify-between cursor-pointer"
                      role="link"
                      tabIndex={0}
                      aria-label={`View ${garage.name}`}
                      onClick={(event) => handleCardNavigate(event, garage.slug, garage.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleCardNavigate(event, garage.slug, garage.id);
                        }
                      }}
                    >
                      {/* Image header banner */}
                      <div className="relative h-44 bg-linear-to-br from-slate-900 via-slate-800 to-blue-950 overflow-hidden">
                        {firstImage ? (
                          <Image
                            src={firstImage.url}
                            alt={firstImage.alt || garage.name}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <Award size={40} className="text-blue-400/60 mx-auto mb-1.5" />
                              <span className="text-xs font-bold text-slate-300">Verified Workshop</span>
                            </div>
                          </div>
                        )}

                        {/* Top Badges */}
                        <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                          <span className="bg-slate-950/80 backdrop-blur-md text-teal-300 text-[11px] font-bold px-2.5 py-1 rounded-full border border-teal-500/30 flex items-center gap-1">
                            <ShieldCheck size={13} className="text-teal-400" /> Verified
                          </span>
                        </div>

                        {garage.distance !== null && (
                          <div className="absolute top-3.5 right-3.5 bg-emerald-600/90 backdrop-blur-md text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
                            <MapPin size={12} />
                            {garage.distance} mi
                          </div>
                        )}

                        {garage.rating && (
                          <div className="absolute bottom-3.5 left-3.5 bg-amber-400 text-slate-950 px-2.5 py-0.5 rounded-full font-black text-xs shadow-md flex items-center gap-1">
                            <Star size={12} fill="currentColor" />
                            {garage.rating}
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="p-6 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-lg font-extrabold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1">
                            {garage.name}
                          </h3>

                          {/* Location box */}
                          <div className="bg-slate-50 rounded-xl p-3 my-3 border border-slate-200/80 flex items-start gap-2">
                            <MapPin size={16} className="text-blue-600 shrink-0 mt-0.5" />
                            <div className="text-xs text-slate-700 leading-snug">
                              <span className="font-bold text-slate-900 block">
                                {garage.city || garage.county || "United Kingdom"}
                              </span>
                              <span className="text-slate-500">
                                {garage.address || garage.postcode || "UK Registered Location"}
                              </span>
                            </div>
                          </div>

                          {/* Contact snippets */}
                          <div className="space-y-1.5 mb-4">
                            {garage.phone && (
                              <a
                                href={`tel:${garage.phone}`}
                                className="flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600 transition-colors"
                              >
                                <span className="w-5 h-5 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                  <Phone size={11} />
                                </span>
                                <span className="font-semibold">{garage.phone}</span>
                              </a>
                            )}
                            {garage.email && (
                              <a
                                href={`mailto:${garage.email}`}
                                className="flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600 transition-colors truncate"
                              >
                                <span className="w-5 h-5 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                  <Mail size={11} />
                                </span>
                                <span className="font-medium truncate">{garage.email}</span>
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Button */}
                        <div className="pt-3 border-t border-slate-100">
                          <Link
                            href={`/garages/${garage.slug || garage.id}`}
                            className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-md shadow-blue-500/20 hover:shadow-blue-500/40 flex items-center justify-center gap-1.5 text-xs sm:text-sm group/btn"
                          >
                            <span>View Garage Profile</span>
                            <ChevronRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-14 flex flex-col items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setCurrentPage(Math.max(1, currentPage - 1));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`w-9 h-9 rounded-xl font-bold text-sm transition-all ${
                            currentPage === page
                              ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                              : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        setCurrentPage(Math.min(totalPages, currentPage + 1));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next →
                    </button>
                  </div>

                  <p className="text-xs text-slate-500">
                    Page <span className="font-bold text-slate-900">{currentPage}</span> of{" "}
                    <span className="font-bold text-slate-900">{totalPages}</span>
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── 4. SIGNATURE BOTTOM CTA FLOATING CARD ──────────────────────── */}
        <section className="py-14 sm:py-20 bg-slate-50 relative overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-3xl p-8 sm:p-14 md:p-16 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 border border-slate-800 shadow-2xl shadow-blue-950/40 text-white text-center space-y-6">
              
              {/* Ambient inner glows */}
              <div className="absolute top-0 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

              <div className="relative z-10 space-y-6 max-w-3xl mx-auto">
                <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
                  GROW YOUR WORKSHOP
                </span>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
                  Own an MOT Station or Workshop?{" "}
                  <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
                    Join GarageBoost Today
                  </span>
                </h2>

                <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed">
                  Get listed in our verified UK directory, automate aggregator email intake for BookMyGarage and ClickMechanic, and recover lost MOT revenue.
                </p>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    href="/signup"
                    className="group w-full sm:w-auto bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-9 py-4 rounded-xl font-bold text-base sm:text-lg shadow-xl shadow-blue-500/40 hover:shadow-blue-500/60 hover:scale-105 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Start 14-Day Free Trial</span>
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                  </Link>
                  <Link
                    href="/features"
                    className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 px-8 py-4 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center gap-2"
                  >
                    View Platform Features
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </PublicLayout>
  );
}